import React, { useState, useEffect } from "react";
import NavMenu from "../NavMenu";
import { Button, Title } from "@bsf/force-ui";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import { LoaderCircle } from 'lucide-react';

const License = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);
    const [error, setError] = useState(null);
    const [isLicenseActive, setIsLicenseActive] = useState(false);

    // Check if license is active on component mount and store token if present
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (params.get('license_action') === 'activate_license' && token) {
            try {
                localStorage.setItem('uavc_license_token', token);
            } catch (e) {
                console.error('Failed to store token', e);
            }
            // Clean URL so token is not exposed repeatedly
            params.delete('license_action');
            params.delete('bsf-inline-license-form');
            params.delete('token');
            params.delete('product_id');
            params.delete('purchase_key');
            params.delete('success');
            params.delete('status');
            const hash = window.location.hash || (uavcLicense?.hash || '#settings');
            const query = params.toString();
            const newUrl = window.location.pathname + (query ? '?' + query : '') + hash;
            window.history.replaceState({}, '', newUrl);
        }

        checkLicenseStatus();
    }, []);

    // Function to check license status via REST API
    const checkLicenseStatus = () => {
        setIsCheckingStatus(true);

        apiFetch({
            path: '/uavc/v1/license-status',
            method: 'GET'
        })
            .then(response => {
                if (response && response.success) {
                    setIsLicenseActive(response.is_active);
                } else {
                    console.error('Invalid response from license status check', response);
                }
                setIsCheckingStatus(false);
            })
            .catch(error => {
                console.error('Error checking license status:', error);
                setIsCheckingStatus(false);
            });
    };

    const handleActivateClick = () => {
        setIsLoading(true);
        setError(null);

        // Get the data from localized script
        const productId = uavcLicense?.product_id || '';
        const url = uavcLicense?.url || '';
        const redirect = uavcLicense?.redirect || '';

        // Use direct AJAX call instead of apiFetch
        jQuery.ajax({
            url: uavcSettingsData.ajax_url,
            type: 'GET',
            dataType: 'json',
            data: {
                action: 'bsf_envato_redirect_url',
                product_id: productId,
                url: url,
                redirect: redirect,
                privacy_consent: 'true',
                terms_conditions_consent: 'true',
                envato_activation_nonce: uavcLicense.envato_activation_nonce || ''
            }
        })
            .done(function (response) {
                if (response && response.success && response.data && response.data.url) {
                    try {
                        const urlObj = new URL(response.data.url);
                        const licenseToken = urlObj.searchParams.get('token');
                        if (licenseToken) {
                            localStorage.setItem('uavc_license_token', licenseToken);
                        }
                    } catch (e) {
                        console.error('Failed to parse token', e);
                    }
                    window.location.href = response.data.url;
                } else {
                    console.error('Invalid response from server', response);
                    setError('Invalid response from server');
                    setIsLoading(false);
                }
            })
            .fail(function (error) {
                console.error('Error activating license:', error);
                setError('Error connecting to the server. Please try again.');
                setIsLoading(false);
            });
    };

    const handleDeactivateClick = () => {
        setIsLoading(true);
        setError(null);

        apiFetch({
            path: '/uavc/v1/license-deactivate',
            method: 'POST'
        })
            .then(response => {
                if (response && response.success) {
                    setIsLicenseActive(false);
                    try {
                        localStorage.removeItem('uavc_license_token');
                    } catch (e) { }
                    checkLicenseStatus();
                } else {
                    setError(response && response.message ? response.message : __('Unable to deactivate license', 'ultimate_vc'));
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error deactivating license:', error);
                setError(__('Error connecting to the server. Please try again.', 'ultimate_vc'));
                setIsLoading(false);
            });
    };

    // Loading state while checking license status
    if (isCheckingStatus) {
        return (
            <>
                <Title
                    description=""
                    icon={null}
                    iconPosition="right"
                    size="sm"
                    tag="h2"
                    title={__('Product License', 'ultimate_vc')}
                />
                <div
                    className="box-border bg-background-primary p-6 rounded-lg"
                    style={{
                        marginTop: "24px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "300px"
                    }}
                >
                    <div style={{ textAlign: "center" }}>
                        <div className="loading-spinner" style={{
                            border: "4px solid rgba(0, 0, 0, 0.1)",
                            borderRadius: "50%",
                            borderTop: "4px solid #2173c4",
                            width: "40px",
                            color: '#0017E1',
                            height: "40px",
                            margin: "0 auto 20px",
                            animation: "spin 1s linear infinite"
                        }}></div>
                        <p>{__('Checking license status...', 'ultimate_vc')}</p>
                        <style>
                            {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
                        </style>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Title
                description=""
                icon={null}
                iconPosition="right"
                size="sm"
                tag="h2"
                title={__('Product License', 'ultimate_vc')}
            />
            <div
                className="box-border bg-background-primary p-6 rounded-lg"
                style={{
                    marginTop: "24px",
                }}
            >
                {!isLicenseActive && (
                    <div>
                        <div
                            shrink={1}
                            className="md:mt-0 flex items-center mb-8 justify-center mt-4">
                            <img
                                src={`${uavcSettingsData.license_url}`}
                                alt="Template Showcase"
                                className="object-contain rounded"
                            />
                        </div>
                        <div style={{ marginTop: '20px' }}>
                            <Title
                                description=""
                                icon={null}
                                iconPosition="right"
                                className="flex items-center justify-center mt-6"
                                size="md"
                                tag="h3"
                                title={__("Activate Your License", "ultimate_vc")}
                            />
                        </div>
                    </div>
                )}


                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "10px 10px",
                    }}
                >
                    {isLicenseActive ? (
                        // Show active license status
                        <div style={{
                            textAlign: "center",
                            backgroundColor: "#fff",
                            backgroundImage: `url(${uavcSettingsData.confetti_banner})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            width: '100%',
                            height: 'auto',
                        }}>
                            <div style={{
                                backgroundColor: "#e7f5ea",
                                borderRadius: "50%",
                                width: "80px",
                                height: "80px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 20px"

                            }}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50" />
                                </svg>
                            </div>
                            <h3 style={{
                                fontSize: "14px",
                                color: "#0017E1",
                            }}>
                                {__('Congratulations!', 'ultimate_vc')}
                            </h3>
                            <h3 style={{
                                fontSize: "18px",
                                color: "#000",
                                marginBottom: "18px"
                            }}>
                                {__('Your License is Active', 'ultimate_vc')}
                            </h3>
                            <p
                                className="text-center"
                                style={{
                                    maxWidth: "600px",
                                    textAlign: "center",
                                    color: "#9CA3AF",
                                    fontSize: "14px",
                                    margin: "0 auto 20px", // Center horizontally and set bottom spacing
                                }}
                            >
                                {__(
                                    'You now have access to premium features, direct plugin updates, template library and official support.',
                                    'ultimate_vc'
                                )}
                            </p>
                            <Button
                                disabled={isLoading}
                                variant="primary"
                                className="bg-[#0017E1] uavc-remove-ring"
                                onClick={handleDeactivateClick}
                                icon={isLoading ? <LoaderCircle className="animate-spin" size={16} /> : null}
                                iconPosition="right"
                                style={{
                                    backgroundColor: "#0017E1",
                                    transition: "background-color 0.3s ease",
                                    marginTop: '8px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {isLoading ? __('Processing', 'ultimate_vc') : __('Deactivate License', 'ultimate_vc')}
                            </Button>
                        </div>
                    ) : (
                        // Show license activation form
                        <>
                            <div className="text-text-secondary" style={{ maxWidth: 600, textAlign: "center", color: "#9CA3AF", fontSize: 14, marginBottom: '10px' }}>
                                {__("Receive all benefits of Ultimate Addons for WPBakery Page Builder. Get access to direct plugin updates, template library and official support", "ultimate_vc")}
                            </div>

                            {error && (
                                <div style={{ color: "#d32f2f", marginBottom: 20, textAlign: "center" }}>
                                    {error}
                                </div>
                            )}

                            <Button
                                disabled={isLoading}
                                variant="primary"
                                className="bg-[#0017E1] uavc-remove-ring"
                                iconPosition="right"
                                icon={isLoading ? <LoaderCircle className="animate-spin" size={16} /> : null}
                                style={{
                                    backgroundColor: "#0017E1",
                                    transition: "background-color 0.3s ease",
                                    marginBottom: '20px',
                                    marginTop: '20px'
                                }}
                                onClick={handleActivateClick}
                            >
                                {isLoading ? __('Processing', 'ultimate_vc') : __('Activate License ', 'ultimate_vc')}
                            </Button>
                            <div style={{ color: "#9CA3AF", fontSize: 14 }}>
                                {__("Don't have a license yet?", "ultimate_vc")}
                                {" "}
                                <a
                                    href="https://codecanyon.net/cart/add_items?item_ids=6892199&ref=BrainstormForce"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#0017E1", textDecoration: "underline" }}
                                >
                                    {__("Purchase Ultimate Addons license here", "ultimate_vc")}
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default License;